<template>
    <div class="employer-payroll-quals">
        <div class="o-ndividualArea c-responsive-fixwidth">
            <div class="o-ndividualTable">
                <div class="c-common-space">
                    <div class="col-md-12 o-no-padding">
                        <h3>Payroll Qualifications</h3>
                        <p class="pq-intro">
                            Four questions that determine how this company pays workers.
                            They are about what you actually do, not what the arrangement
                            is called &mdash; the answers set whether this company appears
                            as a payroll company.
                        </p>

                        <div v-loading="loading">
                            <!-- Q1 -->
                            <div class="form-group pq-q">
                                <label class="common-label-text">
                                    1. Do you cut the checks or originate the ACH to these workers?
                                </label>
                                <div>
                                    <label class="pq-opt"><input type="radio" :value="true" v-model="form.q1_disburses"> Yes</label>
                                    <label class="pq-opt"><input type="radio" :value="false" v-model="form.q1_disburses"> No</label>
                                </div>
                                <span v-if="form.q1_disburses === false" class="pq-stop">
                                    Not a payroll entity &mdash; the remaining questions do not apply.
                                </span>
                            </div>

                            <!-- Q2 -->
                            <div class="form-group pq-q" v-if="form.q1_disburses === true">
                                <label class="common-label-text">
                                    2. Whose EIN is on the W-2 &mdash; yours or your client's?
                                </label>
                                <div>
                                    <label class="pq-opt"><input type="radio" value="own" v-model="form.q2_w2_ein"> Ours</label>
                                    <label class="pq-opt"><input type="radio" value="client" v-model="form.q2_w2_ein"> Our client's</label>
                                </div>
                                <span v-if="form.q2_w2_ein === 'client'" class="pq-stop">
                                    Payroll agent (PSP or Reporting Agent) &mdash; the client is the
                                    employer and owes the tax.
                                </span>
                            </div>

                            <!-- Q3 -->
                            <div class="form-group pq-q" v-if="showQ3">
                                <label class="common-label-text">
                                    3. Do you hire, direct, and have authority to release these
                                    workers, or does your client?
                                </label>
                                <div>
                                    <label class="pq-opt"><input type="radio" value="we" v-model="form.q3_control"> We do</label>
                                    <label class="pq-opt"><input type="radio" value="client" v-model="form.q3_control"> Our client does</label>
                                </div>
                                <span v-if="form.q3_control === 'we'" class="pq-stop">
                                    A self-payrolling employer, not an EOR.
                                </span>
                            </div>

                            <!-- Q4 -->
                            <div class="form-group pq-q" v-if="showQ4">
                                <label class="common-label-text">
                                    4. Are you obligated to pay these workers even if your client
                                    never reimburses you?
                                </label>
                                <div>
                                    <label class="pq-opt"><input type="radio" :value="true" v-model="form.q4_pay_regardless"> Yes</label>
                                    <label class="pq-opt"><input type="radio" :value="false" v-model="form.q4_pay_regardless"> No</label>
                                </div>
                            </div>

                            <!-- Q4a — only for an EOR -->
                            <div class="form-group pq-q" v-if="showQ4a">
                                <label class="common-label-text">
                                    4a. Under which basis?
                                </label>
                                <div>
                                    <label class="pq-opt">
                                        <input type="radio" value="form_2678" v-model="form.q4a_liability">
                                        Form 2678 (&sect;3504 agent &mdash; joint and several liability)
                                    </label>
                                    <label class="pq-opt">
                                        <input type="radio" value="cpeo" v-model="form.q4a_liability">
                                        CPEO certification (sole liability)
                                    </label>
                                </div>
                                <div v-if="form.q4a_liability === 'cpeo'" class="pq-sub">
                                    <label class="common-label-text">CPEO number</label>
                                    <input type="text" class="form-control o-no-radius" maxlength="64"
                                           v-model="form.cpeo_number" placeholder="Verifiable against the IRS published list">
                                </div>
                            </div>

                            <!-- Cross-checks: asked regardless -->
                            <div class="pq-crosschecks" v-if="form.q1_disburses === true">
                                <h5>Cross-checks</h5>
                                <p class="pq-intro">
                                    These should agree with the answers above. If they do not, the
                                    entry is flagged rather than acted on.
                                </p>
                                <div class="form-group">
                                    <label class="common-label-text">Whose legal name is on the pay stub?</label>
                                    <input type="text" class="form-control o-no-radius" maxlength="255"
                                           v-model="form.stub_legal_name" placeholder="Legal entity name as printed">
                                </div>
                                <div class="form-group">
                                    <label class="common-label-text">Who holds the W-4s and I-9s?</label>
                                    <div>
                                        <label class="pq-opt"><input type="radio" value="we" v-model="form.holds_w4_i9"> We do</label>
                                        <label class="pq-opt"><input type="radio" value="client" v-model="form.holds_w4_i9"> Our client does</label>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="common-label-text">Supporting evidence (optional)</label>
                                    <textarea class="form-control o-no-radius" rows="3" v-model="form.evidence_note"
                                        placeholder="e.g. sample pay stub provided; executed Form 8655 / 2678; CPEO number"></textarea>
                                </div>
                            </div>

                            <!-- Current standing.
                                 This shows the LAST SAVED result, not a live read of the
                                 radios above. Ed hit exactly that: answers that qualify,
                                 with the box still reading "Payroll company: No" until Save.
                                 So when the form no longer matches what was saved, the stale
                                 verdict is withheld rather than shown as if it were current. -->
                            <div class="pq-result" v-if="saved.classification || dirty">
                                <template v-if="dirty">
                                    <span class="pq-unsaved">
                                        Answers changed &mdash; press Save to evaluate them.
                                    </span>
                                    <div class="pq-reason" v-if="saved.classification">
                                        Last saved: {{ classificationLabel(saved.classification) }} &mdash;
                                        payroll company {{ isPayrollCompany ? 'Yes' : 'No' }},
                                        Employer {{ isEmployer ? 'Yes' : 'No' }}.
                                    </div>
                                </template>
                                <template v-else>
                                    <span class="pq-result-label">Current classification:</span>
                                    <strong>{{ classificationLabel(saved.classification) }}</strong>
                                    <span v-if="saved.needs_review" class="pq-review">flagged for review</span>
                                    <div v-if="saved.review_reason" class="pq-reason">
                                        <strong>{{ saved.needs_review ? 'Reason:' : 'Note:' }}</strong>
                                        {{ saved.review_reason }}
                                    </div>
                                    <div class="pq-flag">
                                        Payroll company: <strong>{{ isPayrollCompany ? 'Yes' : 'No' }}</strong>
                                        &nbsp;&middot;&nbsp;
                                        Employer: <strong>{{ isEmployer ? 'Yes' : 'No' }}</strong>
                                    </div>
                                </template>
                            </div>

                            <div class="text-right">
                                <a href="javascript: void(0)" class="btn c-solid-btn c-solid-btn--green"
                                   :class="{ 'isDisabled': saving }" @click="save">
                                    <i class="fa fa-check" aria-hidden="true"></i> Save
                                </a>
                            </div>

                            <!-- Ed 2026-08-16: an echo of the recorded validation, so a
                                 company admin can see exactly what was answered and what it
                                 concluded without re-reading the form controls. Renders the
                                 STORED answers only -- it is a record, not a preview, so it
                                 does not move while the form above is being edited. -->
                            <div class="pq-echo" v-if="saved.classification">
                                <h5>Payroll validation record</h5>
                                <table class="pq-echo-table">
                                    <tbody>
                                        <tr v-for="row in echoRows" :key="row.label">
                                            <td class="pq-echo-q">{{ row.label }}</td>
                                            <td class="pq-echo-a" :class="{ 'pq-echo-empty': !row.answered }">
                                                {{ row.value }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div class="pq-echo-result">
                                    <div>
                                        <span class="pq-result-label">Result:</span>
                                        <strong>{{ classificationLabel(saved.classification) }}</strong>
                                        <span v-if="saved.needs_review" class="pq-review">flagged for review</span>
                                    </div>
                                    <div v-if="saved.review_reason" class="pq-reason">{{ saved.review_reason }}</div>
                                    <div class="pq-flag">
                                        Payroll company: <strong>{{ isPayrollCompany ? 'Yes' : 'No' }}</strong>
                                        &nbsp;&middot;&nbsp;
                                        Employer: <strong>{{ isEmployer ? 'Yes' : 'No' }}</strong>
                                        <span v-if="saved.needs_review" class="pq-reason">
                                            (left unchanged &mdash; a flagged entry sets neither)
                                        </span>
                                    </div>
                                    <div class="pq-echo-by" v-if="saved.completed_at">
                                        Recorded by {{ saved.completed_by_name || 'unknown' }}
                                        on {{ formatStamp(saved.completed_at) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import service from '../../services.js';

    export default {
        name: 'employerPayrollQualifications',
        props: ['employerId'],
        data() {
            return {
                loading: false,
                saving: false,
                isPayrollCompany: false,
                isEmployer: false,
                saved: {},
                form: {
                    q1_disburses: null,
                    q2_w2_ein: null,
                    q3_control: null,
                    q4_pay_regardless: null,
                    q4a_liability: null,
                    cpeo_number: '',
                    stub_legal_name: '',
                    holds_w4_i9: null,
                    evidence_note: ''
                }
            };
        },
        computed: {
            ...mapGetters({
                userToken: 'userToken',
                userRole: 'userRole',
                userClientID: 'userClientID',
            }),
            // Each question is a gate: a terminal answer stops the sequence, so
            // later questions are not merely hidden -- they do not apply.
            showQ3() {
                return this.form.q1_disburses === true && this.form.q2_w2_ein === 'own';
            },
            showQ4() {
                return this.showQ3 && this.form.q3_control === 'client';
            },
            showQ4a() {
                return this.showQ4 && this.form.q4_pay_regardless === true;
            },
            /**
             * Does the form still match what was saved?
             *
             * Deliberately NOT a client-side re-implementation of the decision
             * rules. Those live server-side in EmployerPayrollQual::classify() and
             * duplicating them here is how the two drift apart (cf. #1656, where
             * duplicated availability logic recurred for months). The component
             * only needs to know "is the displayed verdict still about these
             * answers", which is a comparison, not a classification.
             */
            /**
             * The stored answers, echoed back in the order they were asked.
             *
             * Renders whatever is stored and shows an em dash where nothing was
             * recorded -- it does NOT re-derive which questions "applied", because
             * that would be the stop-logic living in a second place. Storage is the
             * record; this just reads it out.
             */
            echoRows() {
                var s = this.saved || {};
                var yn = function (v) {
                    return (v === null || v === undefined) ? null : (v ? 'Yes' : 'No');
                };
                var who = function (v) {
                    if (v === 'we') return 'We do';
                    if (v === 'client') return 'Our client does';
                    return null;
                };
                var ein = function (v) {
                    if (v === 'own') return 'Ours';
                    if (v === 'client') return "Our client's";
                    return null;
                };
                var basis = function (v) {
                    if (v === 'form_2678') return 'Form 2678 (\u00a73504 agent \u2014 joint and several)';
                    if (v === 'cpeo') return 'CPEO certification (sole liability)';
                    return null;
                };
                var rows = [
                    ['1. Cuts the checks / originates the ACH', yn(s.q1_disburses)],
                    ['2. EIN on the W-2', ein(s.q2_w2_ein)],
                    ['3. Hires, directs, can release the workers', who(s.q3_control)],
                    ['4. Pays the workers regardless of reimbursement', yn(s.q4_pay_regardless)],
                    ['4a. Liability basis', basis(s.q4a_liability)],
                    ['CPEO number', s.cpeo_number || null],
                    ['Legal name on the pay stub', s.stub_legal_name || null],
                    ['Holds the W-4s and I-9s', who(s.holds_w4_i9)],
                    ['Supporting evidence', s.evidence_note || null]
                ];
                return rows.map(function (r) {
                    return { label: r[0], value: r[1] === null ? '\u2014' : r[1], answered: r[1] !== null };
                });
            },

            dirty() {
                var f = this.form, s = this.saved;
                if (!s || !s.classification) {
                    // nothing saved yet: dirty as soon as anything is answered
                    return f.q1_disburses !== null;
                }
                return f.q1_disburses !== !!s.q1_disburses
                    || (f.q2_w2_ein || null) !== (s.q2_w2_ein || null)
                    || (f.q3_control || null) !== (s.q3_control || null)
                    || this.triState(f.q4_pay_regardless) !== this.triState(s.q4_pay_regardless)
                    || (f.q4a_liability || null) !== (s.q4a_liability || null)
                    || (f.holds_w4_i9 || null) !== (s.holds_w4_i9 || null)
                    || (f.cpeo_number || '') !== (s.cpeo_number || '')
                    || (f.stub_legal_name || '') !== (s.stub_legal_name || '')
                    || (f.evidence_note || '') !== (s.evidence_note || '');
            }
        },
        methods: {
            // null stays null; anything else collapses to a real boolean, so a
            // stored 0/1 compares equal to a form true/false.
            triState(v) {
                return (v === null || v === undefined) ? null : !!v;
            },

            formatStamp(v) {
                if (!v) return '';
                var d = new Date(v);
                if (isNaN(d.getTime())) return v;
                return d.toLocaleString();
            },

            classificationLabel(c) {
                var map = {
                    not_payroll: 'Not a payroll entity',
                    payroll_agent: 'Payroll agent (PSP / Reporting Agent)',
                    self_payrolling: 'Self-payrolling employer',
                    eor: 'Employer of Record',
                    needs_review: 'Needs review'
                };
                return map[c] || c;
            },

            headers() {
                return {
                    'Authorization': this.userToken,
                    'cs3-context-role': this.userRole,
                    'cs3-context-clientId': this.userClientID,
                };
            },

            load() {
                if (!this.employerId) return;
                this.loading = true;
                service.get('employer/' + this.employerId + '/payroll-qualifications', this.headers())
                    .then((res) => {
                        this.loading = false;
                        var r = (res.data && res.data.result) ? res.data.result : {};
                        this.isPayrollCompany = !!r.is_payroll_company;
                        this.isEmployer = !!r.is_employer;
                        var q = r.qualification;
                        if (q) {
                            this.saved = q;
                            this.form.q1_disburses = q.q1_disburses;
                            this.form.q2_w2_ein = q.q2_w2_ein;
                            this.form.q3_control = q.q3_control;
                            this.form.q4_pay_regardless = q.q4_pay_regardless;
                            this.form.q4a_liability = q.q4a_liability;
                            this.form.cpeo_number = q.cpeo_number || '';
                            this.form.stub_legal_name = q.stub_legal_name || '';
                            this.form.holds_w4_i9 = q.holds_w4_i9;
                            this.form.evidence_note = q.evidence_note || '';
                        }
                    })
                    .catch(() => { this.loading = false; });
            },

            save() {
                if (this.saving) return;
                if (this.form.q1_disburses === null) {
                    this.$message({ type: 'error', message: 'Answer question 1 before saving.' });
                    return;
                }
                this.saving = true;
                service.put('employer/' + this.employerId + '/payroll-qualifications', this.form, this.headers())
                    .then((res) => {
                        this.saving = false;
                        var d = res.data || {};
                        if (d.status_code == '1') {
                            var r = d.result || {};
                            this.saved = r.qualification || {};
                            this.isPayrollCompany = !!r.is_payroll_company;
                            this.isEmployer = !!r.is_employer;
                            // warning, not success, when nothing was provisioned
                            this.$message({
                                type: this.saved.needs_review ? 'warning' : 'success',
                                message: d.status_message
                            });
                        } else {
                            this.$message({ type: 'error', message: this.firstError(d) });
                        }
                    })
                    .catch((err) => {
                        this.saving = false;
                        var d = (err && err.response && err.response.data) ? err.response.data : {};
                        this.$message({ type: 'error', message: this.firstError(d) });
                    });
            },

            // status_message is a MessageBag on a 422 and a string otherwise --
            // the shape that produced the blank toast in !505.
            firstError(d) {
                var fallback = 'Could not save the payroll qualifications.';
                var m = d ? d.status_message : null;
                if (!m) return fallback;
                if (typeof m === 'string') return m;
                if (Array.isArray(m)) return m[0] || fallback;
                var msgs = [];
                Object.keys(m).forEach(function (k) {
                    var v = m[k];
                    if (Array.isArray(v)) { msgs = msgs.concat(v); }
                    else if (typeof v === 'string') { msgs.push(v); }
                });
                return msgs.length ? msgs.join(' ') : fallback;
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style scoped>
.pq-intro { color: #777; font-size: 0.92em; margin-bottom: 12px; }
.pq-q { margin-bottom: 14px; }
.pq-opt { display: inline-block; margin-right: 18px; font-weight: normal; }
.pq-opt input { margin-right: 5px; }
.pq-sub { margin-top: 8px; max-width: 420px; }
.pq-stop { display: block; margin-top: 4px; color: #b06a00; font-size: 0.9em; }
.pq-crosschecks { margin-top: 18px; padding-top: 12px; border-top: 1px solid #e5e5e5; }
.pq-result { margin: 16px 0; padding: 10px 12px; background: rgba(0,0,0,0.03); border-radius: 3px; }
.pq-result-label { color: #777; margin-right: 4px; }
.pq-review { margin-left: 8px; color: #b06a00; font-weight: 600; }
.pq-unsaved { color: #b06a00; font-weight: 600; }
.pq-echo { margin-top: 20px; padding-top: 14px; border-top: 1px solid #e5e5e5; }
.pq-echo-table { width: 100%; max-width: 680px; margin-bottom: 10px; }
.pq-echo-table td { padding: 3px 0; vertical-align: top; }
.pq-echo-q { color: #777; padding-right: 14px !important; width: 60%; }
.pq-echo-a { font-weight: 600; }
.pq-echo-empty { font-weight: normal; color: #aaa; }
.pq-echo-result { padding: 10px 12px; background: rgba(0,0,0,0.03); border-radius: 3px; }
.pq-echo-by { margin-top: 6px; color: #888; font-size: 0.88em; }
.pq-reason { margin-top: 4px; color: #777; font-size: 0.9em; }
.pq-flag { margin-top: 6px; }
</style>
